/**
 * Componente principal de la aplicación ProKidsApp
 *
 * Este componente configura:
 * - Proveedores de contexto (autenticación y notificaciones)
 * - Enrutamiento con React Router
 * - Layouts para diferentes tipos de usuarios
 * - Protección de rutas basada en roles
 */
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { GTMProvider } from './context/GTMContext';
import PrivateRoute from './components/PrivateRoute';
import useWindowSize from './hooks/useWindowSize';
import Header from './components/Header';
import Footer from './components/Footer';
import MobileBottomNav from './components/MobileBottomNav';
import TutorNavigation from './components/TutorNavigation';
import PlayerNavigation from './components/PlayerNavigation';
import CoachNavigation from './components/CoachNavigation';
import ToastNotifications from './components/common/Notifications';

// Estilos
import './styles/admin.css';
import './styles/auth.css';
import './styles/modal.css';
import './styles/mobile-layout.css';
import './styles/header.css';
import './styles/mobile-fixes.css'; // Fixes específicos para móviles

// Páginas públicas
import Login from './pages/Login';
import Register from './pages/Register';
import DirectProfile from './pages/DirectProfile';
import EditProfile from './pages/EditProfile';
import ProfileRedirect from './pages/ProfileRedirect';
import UserProfileDebug from './pages/UserProfileDebug';
// import Home from './pages/Home'; // Home redirige a SummerEdition temporalmente
import Help from './pages/Help';
import JornadasGestion from './pages/JornadasGestion';
import Categories from './pages/Categories';
import MatchDetail from './pages/MatchDetail';
import MatchCheckout from './pages/MatchCheckout';
import ProductCheckout from './pages/ProductCheckout';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentError from './pages/PaymentError';
import RedsysRedirect from './pages/RedsysRedirect';
import Compras from './pages/Wallet';
import VenuesMap from './pages/VenuesMap';
import NotificationsPage from './pages/Notifications';
import About from './pages/About';
import Contact from './pages/Contact';
import Products from './pages/Products';
import SummerEdition from './pages/SummerEdition';
import ProPoints from './pages/ProPoints';
import ProPointsSummerEdition from './pages/ProPointsSummerEdition';
import FAQ from './pages/FAQ';
import LigaProkids from './pages/LigaProkids';
import Noticias from './pages/Noticias';
import ShareLogros from './pages/public/ShareLogros';
import ShareLogroIndividual from './pages/public/ShareLogroIndividual';
import ShareNivel from './pages/public/ShareNivel';
import ShareLogroEspecial from './pages/public/ShareLogroEspecial';
// Componentes y páginas de jugadores
import PlayerLayout from './components/PlayerLayout';
import PlayerProfile from './pages/player/PlayerProfile';
import PlayerJornadas from './pages/player/PlayerJornadas';
import PlayerProPoints from './pages/player/PlayerProPoints';
import PlayerNotificaciones from './pages/player/PlayerNotificaciones';
import PlayerAyuda from './pages/player/PlayerAyuda';

// Componentes y páginas de entrenadores
import CoachLayout from './components/CoachLayout';
import CoachDashboard from './pages/coach/CoachDashboard';
import CoachPartidos from './pages/coach/CoachPartidos';
import CoachPartidoDetalles from './pages/coach/CoachPartidoDetalles';
import CoachAsignarProPoints from './pages/coach/CoachAsignarProPoints';
import CoachEvaluaciones from './pages/coach/CoachEvaluaciones';
import CoachPerfil from './pages/coach/CoachPerfil';

import AvisoLegal from './pages/AvisoLegal';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Cookies from './pages/CookiesPolicy';

// Páginas de autenticación
import EmailVerification from './pages/EmailVerification';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Páginas de administración
import Dashboard from './pages/admin/Dashboard';
import AdminProfile from './pages/admin/AdminProfile';
import UserEdit from './pages/admin/UserEdit';
import UserView from './pages/admin/UserView';
import UserDetails from './pages/admin/UserDetails';
import TournamentManagement from './pages/admin/TournamentManagement';
import TournamentPlayers from './pages/admin/TournamentPlayers';
import TournamentProPoints from './pages/admin/TournamentProPoints';
import CategoryManagementSimple from './pages/admin/CategoryManagementSimple';

import VenueManagementUnified from './pages/admin/VenueManagementUnified';
import AchievementsManagement from './pages/admin/AchievementsManagement';
import ProductManagementUnified from './pages/admin/ProductManagementUnified';
import TransferManagement from './pages/admin/TransferManagement';
import ScoutManagement from './pages/admin/ScoutManagement';
import EquipacionManagement from './pages/admin/EquipacionManagement';
import InscripcionManagementSimple from './pages/admin/InscripcionManagementSimple';
import ConsumirJornada from './pages/admin/ConsumirJornada';
import CambioDeSede from './pages/admin/CambioDeSede';
import PromosManagement from './pages/admin/PromosManagement';
import VerificarIntegridad from './pages/admin/VerificarIntegridad';
import Reports from './pages/admin/Reports';
import Settings from './pages/admin/Settings';
import UnifiedUserManagement from './pages/admin/UnifiedUserManagement';
import TemporadaManagement from './pages/admin/TemporadaManagement';

// Páginas de tutor
import TutorDashboard from './pages/tutor/TutorDashboard';
import TutorProfile from './pages/tutor/TutorProfile';
import FutbolistaManagement from './pages/tutor/FutbolistaManagement';
import FutbolistaForm from './pages/tutor/FutbolistaForm';
import FutbolistaDetail from './pages/tutor/FutbolistaDetail';
import FutbolistaCredenciales from './pages/tutor/FutbolistaCredenciales';
import FutbolistaProPoints from './pages/tutor/FutbolistaProPoints';

// Página temporal para rutas no implementadas
const NotImplemented = () => {
  const location = useLocation();

  return (
    <div className="container" style={{ padding: '50px 0', textAlign: 'center' }}>
      <h2>Página en desarrollo</h2>
      <p>Esta funcionalidad estará disponible próximamente.</p>
      <p>Ruta actual: {location.pathname}</p>
    </div>
  );
};

// Página para usuarios no autorizados
const Unauthorized = () => {
  const navigate = useNavigate();

  return (
    <div className="container" style={{ padding: '50px 0', textAlign: 'center' }}>
      <h2>Acceso no autorizado</h2>
      <p>No tienes permisos para acceder a esta página.</p>
      <p>Si crees que esto es un error, por favor contacta con el administrador.</p>

      <div style={{ marginTop: '20px' }}>
        <button
          className="btn btn-primary"
          onClick={() => navigate('/profile-debug')}
          style={{ marginRight: '10px' }}
        >
          Ver información de depuración
        </button>

        <button className="btn btn-secondary" onClick={() => navigate('/')}>
          Volver al inicio
        </button>
      </div>
    </div>
  );
};

/**
 * Componente de layout que controla la visualización del header y footer
 *
 * Muestra el header y footer solo en las rutas que no son de administración
 * Aplica diferentes clases CSS dependiendo del tipo de ruta
 *
 * @param {Object} props - Propiedades del componente
 * @param {React.ReactNode} props.children - Componentes hijos a renderizar
 */
const AppLayout = ({ children }) => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { isMobile } = useWindowSize();
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Estilos inline para asegurar que el layout ocupe todo el ancho disponible
  const appStyles = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    width: '100%',
    margin: 0,
    padding: 0,
    boxSizing: 'border-box',
  };

  const mainStyles = {
    flex: 1,
    width: '100%',
    margin: 0,
    padding: 0,
    boxSizing: 'border-box',
  };

  // WhatsApp flotante: solo en landing page, ningún contexto de apilamiento intermedio
  const isLandingPage = location.pathname === '/' || location.pathname === '/liga-prokids';

  return (
    <div className="app" style={appStyles}>
      {!isAdminRoute && <Header />}
      <main className={isAdminRoute ? '' : 'main-content'} style={mainStyles}>
        {!isAdminRoute && isAuthenticated && user && (
          user.role === 'player' ? <PlayerNavigation /> :
          user.role === 'coach' ? <CoachNavigation /> :
          <TutorNavigation />
        )}
        {children}
      </main>
      {!isAdminRoute && <Footer />}
      {!isAdminRoute && !isAuthenticated && <MobileBottomNav />}
      {!isAdminRoute && isLandingPage && (
        <a
          href="https://wa.me/34604972879?text=Hola%2C%20tengo%20una%20duda%20sobre%20Liga%20ProKids."
          target="_blank"
          rel="noopener noreferrer"
          title="Contactar por WhatsApp"
          style={{
            position: 'fixed',
            /* En móvil: sube sobre la barra de navegación inferior (~60px) */
            bottom: isMobile ? '82px' : '28px',
            right: isMobile ? '16px' : '24px',
            zIndex: 99999,
            width: isMobile ? '52px' : '58px',
            height: isMobile ? '52px' : '58px',
            borderRadius: '50%',
            backgroundColor: '#25D366',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(37,211,102,0.5)',
            textDecoration: 'none',
          }}
        >
          <i className="fa-brands fa-whatsapp" style={{ fontSize: isMobile ? '28px' : '32px', color: '#FFFFFF' }}></i>
        </a>
      )}
    </div>
  );
};

AppLayout.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Componente principal de la aplicación
 *
 * Configura los proveedores de contexto y el enrutamiento
 * Define todas las rutas de la aplicación y su protección
 *
 * @returns {React.ReactElement} El componente App renderizado
 */
function App() {
  // Componente para manejar errores en la aplicación
  class ErrorBoundary extends React.Component {
    constructor(props) {
      super(props);
      this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(_error) {
      // Actualizar el estado para mostrar la UI de fallback
      return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
      // Puedes registrar el error en un servicio de reporte de errores
      console.error('Error en la aplicación:', error, errorInfo);
      this.setState({ error, errorInfo });
    }

    render() {
      if (this.state.hasError) {
        // Puedes renderizar cualquier UI de fallback
        return (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <h2>Algo salió mal.</h2>
            <p>Ha ocurrido un error en la aplicación. Por favor, intenta recargar la página.</p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 20px',
                backgroundColor: '#061156',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                marginTop: '20px',
              }}
            >
              Recargar página
            </button>
            {this.state.error && (
              <details style={{ marginTop: '20px', textAlign: 'left' }}>
                <summary>Detalles del error (para desarrolladores)</summary>
                <p>{this.state.error.toString()}</p>
                <p>Componente: {this.state.errorInfo && this.state.errorInfo.componentStack}</p>
              </details>
            )}
          </div>
        );
      }

      return this.props.children;
    }
  }

  ErrorBoundary.propTypes = {
    children: PropTypes.node.isRequired,
  };

  // App component rendering

  return (
    <Router>
      <ErrorBoundary>
        <AuthProvider>
          <NotificationProvider>
            <GTMProvider>
            <AppLayout>
              <ToastNotifications />
              <Routes>
                {/* Rutas públicas */}
                <Route path="/" element={<LigaProkids />} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/liga-prokids" element={<LigaProkids />} />
                <Route path="/summer-edition" element={<SummerEdition />} />
                <Route path="/propoints" element={<ProPoints />} />
                <Route path="/propoints-summer-edition" element={<ProPointsSummerEdition />} />
                <Route path="/noticias" element={<Noticias />} />
                <Route path="/preguntas-frecuentes" element={<FAQ />} />
                <Route path="/aviso-legal" element={<AvisoLegal />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/cookies" element={<Cookies />} />
                <Route path="/products" element={<Products />} />
                <Route path="/products/:id/checkout" element={<ProductCheckout />} />
                <Route path="/seguro/checkout" element={<ProductCheckout />} />
                <Route path="/categories" element={<Categories />} />
                <Route path="/matches" element={<JornadasGestion />} />
                <Route path="/match/:id" element={<MatchDetail />} />
                <Route path="/match/:id/checkout" element={<MatchCheckout />} />
                <Route path="/payment/success/:transactionId" element={<PaymentSuccess />} />
                <Route path="/payment/error/:transactionId" element={<PaymentError />} />
                <Route path="/payment/redsys/:transactionId" element={<RedsysRedirect />} />
                <Route path="/venues-map" element={<VenuesMap />} />
                <Route path="/unauthorized" element={<Unauthorized />} />

                {/* Rutas públicas para compartir logros */}
                <Route path="/share/partido/:partidoId/futbolista/:futbolistaId" element={<ShareLogros />} />
                <Route path="/share/partido/:partidoId/futbolista/:futbolistaId/logro/:logroNombre" element={<ShareLogroIndividual />} />

                {/* Ruta pública para compartir nivel */}
                <Route path="/share/futbolista/:futbolistaId/nivel" element={<ShareNivel />} />

                {/* Ruta pública para compartir logros especiales */}
                <Route path="/share/futbolista/:futbolistaId/logro-especial/:logroEspecialId" element={<ShareLogroEspecial />} />

                {/* Rutas de autenticación */}
                <Route path="/verify-email" element={<EmailVerification />} />
                <Route path="/verify-email/:token" element={<EmailVerification />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />

                {/* Rutas protegidas para usuarios autenticados */}
                <Route
                  element={<PrivateRoute requiredRoles={['user', 'parent', 'coach', 'admin']} />}
                >
                  <Route path="/profile" element={<ProfileRedirect />} />
                  <Route path="/wallet" element={<Compras />} />
                  <Route path="/notifications" element={<NotificationsPage />} />
                  <Route path="/help" element={<Help />} />
                </Route>

                {/* Rutas alternativas para depuración del perfil (sin protección) */}
                <Route path="/profile-debug" element={<UserProfileDebug />} />

                {/* Ruta principal para el perfil */}
                <Route path="/direct-profile" element={<DirectProfile />} />
                <Route path="/edit-profile" element={<EditProfile />} />

                {/* Rutas protegidas para administradores */}
                <Route element={<PrivateRoute requiredRoles={['admin']} />}>
                  <Route path="/admin" element={<Dashboard />} />
                  <Route path="/admin/profile" element={<AdminProfile />} />
                  <Route path="/admin/users" element={<UnifiedUserManagement />} />
                  <Route path="/admin/users/:id" element={<UserEdit />} />
                  <Route path="/admin/users/:id/view" element={<UserView />} />
                  <Route path="/admin/users/:id/details" element={<UserDetails />} />
                  <Route path="/admin/matches" element={<TournamentManagement />} />
                  <Route path="/admin/tournaments/:torneoId/players" element={<TournamentPlayers />} />
                  <Route path="/admin/tournaments/:torneoId/propoints" element={<TournamentProPoints />} />
                  <Route path="/admin/categories" element={<CategoryManagementSimple />} />
                  <Route path="/admin/venues" element={<VenueManagementUnified />} />
                  <Route path="/admin/achievements" element={<AchievementsManagement />} />
                  <Route path="/admin/products" element={<ProductManagementUnified />} />
                  <Route path="/admin/transfers" element={<TransferManagement />} />
                  <Route path="/admin/scouts" element={<ScoutManagement />} />
                  <Route path="/admin/equipaciones" element={<EquipacionManagement />} />
                  <Route path="/admin/inscripciones" element={<InscripcionManagementSimple />} />
                  <Route path="/admin/consumir-jornada" element={<ConsumirJornada />} />
                  <Route path="/admin/cambio-sede" element={<CambioDeSede />} />
                  <Route path="/admin/promos" element={<PromosManagement />} />
                  <Route path="/admin/verificar-integridad" element={<VerificarIntegridad />} />
                  <Route path="/admin/temporadas" element={<TemporadaManagement />} />
                  <Route path="/admin/reports" element={<Reports />} />
                  <Route path="/admin/settings" element={<Settings />} />
                </Route>

                {/* Rutas protegidas para entrenadores */}
                <Route element={<PrivateRoute requiredRoles={['coach']} />}>
                  <Route path="/coach" element={<CoachLayout />}>
                    <Route index element={<Navigate to="/coach/dashboard" replace />} />
                    <Route path="dashboard" element={<CoachDashboard />} />
                    <Route path="partidos" element={<CoachPartidos />} />
                    <Route path="partidos/:id" element={<CoachPartidoDetalles />} />
                <Route path="partidos/:id/propoints" element={<CoachAsignarProPoints />} />
                    <Route path="evaluaciones" element={<CoachEvaluaciones />} />
                    <Route path="perfil" element={<CoachPerfil />} />
                  </Route>
                </Route>

                {/* Rutas protegidas para tutores/padres */}
                <Route element={<PrivateRoute requiredRoles={['parent']} />}>
                  <Route path="/tutor" element={<TutorDashboard />} />
                  <Route path="/tutor/dashboard" element={<TutorDashboard />} />
                  <Route path="/tutor/profile" element={<TutorProfile />} />
                  <Route path="/tutor/futbolistas" element={<FutbolistaManagement />} />
                  <Route path="/tutor/futbolistas/nuevo" element={<FutbolistaForm />} />
                  <Route path="/tutor/futbolistas/:id" element={<FutbolistaDetail />} />
                  <Route path="/tutor/futbolistas/:id/credenciales" element={<FutbolistaCredenciales />} />
                  <Route path="/tutor/futbolistas/:id/propoints" element={<FutbolistaProPoints />} />
                  <Route path="/tutor/futbolistas/:id/editar" element={<FutbolistaForm />} />
                </Route>

                {/* Rutas protegidas para futbolistas */}
                <Route element={<PrivateRoute requiredRoles={['player']} />}>
                  <Route path="/player" element={<PlayerLayout />}>
                    <Route index element={<Navigate to="/player/perfil" replace />} />
                    <Route path="perfil" element={<PlayerProfile />} />
                    <Route path="jornadas" element={<PlayerJornadas />} />
                    <Route path="propoints" element={<PlayerProPoints />} />
                    <Route path="notificaciones" element={<PlayerNotificaciones />} />
                    <Route path="ayuda" element={<PlayerAyuda />} />
                  </Route>
                  {/* Ruta de compatibilidad */}
                  <Route path="/futbolista" element={<Navigate to="/player" replace />} />
                </Route>

                {/* Ruta para páginas no encontradas */}
                <Route path="*" element={<NotImplemented />} />
              </Routes>
            </AppLayout>
            </GTMProvider>
          </NotificationProvider>
        </AuthProvider>
      </ErrorBoundary>
    </Router>
  );
}

export default App;
